"use client";

import { useState } from "react";
import {
  Building2,
  BadgeCheck,
  MonitorPlay,
  Zap,
  CircleCheck,
  Trophy,
  Target,
  Eye,
  Building,
} from "lucide-react";
import { useGetOverallAnalyticsQuery } from "@/store/slices/apiSlice";
import AnalyticsStatsCard from "@/components/analytics/AnalyticsStatsCard";
import AnalyticsLineChart from "@/components/analytics/AnalyticsLineChart";
import TopListCard from "@/components/analytics/TopListCard";
// import TenantBreakdownTable from "@/components/analytics/TenantBreakdownTable";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import DataTable from "@/components/DataTable";
import CircleLoader from "@/components/common/ActionLoader";

const Analytics = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetOverallAnalyticsQuery();

  const analyticsData = data?.response?.data?.counters;

  const sessionsChartData =
    data?.response?.data?.sessionsOverTime?.map((item) => ({
      date: new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
      sessions: item.sessions,
      completions: item.completed,
    })) || [];

  const completionRateData =
    data?.response?.data?.completionRateTrend?.map((item) => ({
      date: new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
      rate: item.completionRate,
    })) || [];

  const topTenants =
    data?.response?.data?.topTenantsByUsage?.map((item, index) => ({
      id: index + 1,
      name: item.tenantName,
      description: `${item.sessions} sessions`,
      tenantId: item.tenantId,
    })) || [];
  const topExperiences =
    data?.response?.data?.topExperiencesByUsage?.map((item, index) => ({
      id: index + 1,
      name: item.experienceName,
      description: `${item.completionRate}% completion`,
      experienceId: item.experienceId,
      activeUsers: item.activeUsers,
    })) || [];
  const tenantBreakdown =
    data?.response?.data?.tenantBreakdown?.map((item) => ({
      id: item.tenantId,
      name: item.tenantName,
      sessions: item.sessions,
      completionRate: item.completionRate,
      activeUsers: item.activeUsers,
    })) || [];
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewAnalytics = (tenant) => {
    navigate("/analytics/tenant-details", {
      state: {
        tenant,
        tenantId: tenant.id,
      },
    });
  };

  const stats = [
    {
      label: "Total Tenants",
      value: analyticsData?.totalTenants || 0,
      icon: Building2,
      iconClassName: "text-[#9C6BFF]",
    },
    {
      label: "Active Tenants",
      value: analyticsData?.activeTenants || 0,
      icon: BadgeCheck,
      iconClassName: "text-[#F4D84A]",
    },
    {
      label: "Total Experiences",
      value: analyticsData?.totalExperiences || 0,
      icon: MonitorPlay,
      iconClassName: "text-[#67D8FF]",
    },
    {
      label: "Total Sessions",
      value: analyticsData?.totalSessions || 0,
      icon: Zap,
      iconClassName: "text-[#FF7A00]",
    },
    {
      label: "Avg Completion Rate",
      value: `${analyticsData?.avgCompletionRate || 0}%`,
      icon: CircleCheck,
      iconClassName: "text-[#00E261]",
    },
  ];

  const tableData = tenantBreakdown;
  const columns = [
    {
      header: "Tenant Name",
      accessor: "name",
      render: (row) => (
        <div className="flex min-w-[190px] items-center gap-2 text-neutral-100">
          <Building className="h-4 w-4 text-[#00D3F2]" />
          <span>{row?.name || "-"}</span>
        </div>
      ),
    },
    {
      header: "Sessions",
      accessor: "sessions",
      render: (row) => (
        <div className="text-neutral-100/85">{row?.sessions || "-"}</div>
      ),
    },
    {
      header: "Completion Rate",
      accessor: "completionRate",
      render: (row) => {
        const completionRate = Number(row?.completionRate || 0);

        return (
          <div className="flex min-w-[160px] items-center gap-4">
            <div className="h-2 w-[80px] overflow-hidden rounded-full bg-neutral-100/15">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#20D3E6] to-[#8A3FFC]"
                style={{ width: `${completionRate}%` }}
              />
            </div>

            <span className="text-neutral-100">{completionRate}%</span>
          </div>
        );
      },
    },
    {
      header: "Active Users",
      accessor: "activeUsers",
      render: (row) => (
        <div className="text-neutral-100/85">{row?.activeUsers ?? "-"}</div>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <Button
          type="button"
          variant="outline"
          onClick={() => handleViewAnalytics(row)}
          className="h-8 border-[#00A9D6] bg-transparent px-3 text-xs font-semibold text-neutral-100 hover:bg-neutral-100/5 hover:text-neutral-100"
        >
          <Eye className="mr-1 h-3 w-3" />
          View Analytics
        </Button>
      ),
    },
  ];
  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <CircleLoader loading={isLoading || isFetching} size={64} />
      </div>
    );
  }
  return (
    <div className="min-h-screen  px-6 py-6 text-neutral-100">
      <header className="mb-7">
        <h1 className="mb-3 text-3xl font-bold tracking-tight">Analytics</h1>

        <p className="text-sm font-semibold text-neutral-100/85">
          Monitor overall platform health and usage across all tenants
        </p>
      </header>

      <section className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {stats.map((item) => (
          <AnalyticsStatsCard
            key={item.label}
            label={item.label}
            value={item.value}
            icon={item.icon}
            iconClassName={item.iconClassName}
          />
        ))}
      </section>

      <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <AnalyticsLineChart
          title="Sessions Over Time"
          data={sessionsChartData}
          lines={[
            {
              dataKey: "sessions",
              name: "Sessions",
              stroke: "#00D3F2",
            },
            {
              dataKey: "completions",
              name: "Completions",
              stroke: "#9C6BFF",
            },
          ]}
          yDomain={[0, 200]}
        />

        <AnalyticsLineChart
          title="Completion Rate Trend"
          data={completionRateData}
          lines={[
            {
              dataKey: "rate",
              name: "Rate",
              stroke: "#00C98B",
            },
          ]}
          yDomain={[0, 100]}
          suffix="%"
        />
      </section>

      <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <TopListCard
          title="Top Tenants by Usage"
          icon={Trophy}
          iconClassName="text-[#FFB000]"
          items={topTenants}
        />

        <TopListCard
          title="Top Experiences"
          icon={Target}
          iconClassName="text-[#00D3F2]"
          items={topExperiences}
        />
      </section>

      <section className="rounded-lg bg-[#111827]/90 p-5 shadow-lg">
        <h2 className="mb-5 text-sm font-bold text-neutral-100">
          Tenant Breakdown
        </h2>
        <div className="max-h-[340px] overflow-auto">
          <DataTable
            data={tableData}
            columns={columns}
            // totalPages={totalPages}
            // currentPage={currentPage}
            // onPageChange={handlePageChange}
            isLoading={isLoading || isFetching}
            searchPlaceholder="Search for..."
            bgColor="[#172D45]"
            tableHeaderBg="[#172D45]"
            borderNone={false}
            showPagination={false}
          />
        </div>
      </section>
    </div>
  );
};

export default Analytics;
